<template>
  <v-card>
    <v-card-text class="pa-4">
      <div class="d-flex align-center">
        <div class="field-badge-test__title">{{ $t('onboarding.field.badge-test.title') }}</div>
        <v-spacer />
        <v-btn v-if="running" small text @click="stop">{{ $t('onboarding.field.badge-test.stop') }}</v-btn>
      </div>
      <p class="text-body-2 text--secondary">{{ $t('onboarding.field.badge-test.hint') }}</p>

      <v-btn v-if="!running" small depressed color="primary" :loading="starting" @click="start">
        <v-icon left small>mdi-card-account-details-outline</v-icon>{{ $t(badges.length || expired ? 'onboarding.field.badge-test.again' : 'onboarding.field.badge-test.start') }}
      </v-btn>
      <div v-else class="d-flex align-center text-body-2 py-1">
        <v-progress-circular indeterminate size="22" width="2" color="primary" class="mr-3" />
        {{ $t('onboarding.field.badge-test.waiting', { seconds: remaining }) }}
      </div>

      <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">{{ error }}</v-alert>
      <p v-if="expired && !badges.length" class="text-body-2 text--secondary mt-3 mb-0">{{ $t('onboarding.field.badge-test.nothing') }}</p>

      <!-- Le badge lu, à qui il est, et ce qu'on a répondu à la borne. -->
      <v-alert
          v-for="(badge, index) in badges"
          :key="index"
          :type="badge.accepted ? 'success' : 'error'"
          text
          dense
          class="mt-3 mb-0"
      >
        <div class="font-weight-medium">{{ $t(badge.accepted ? 'onboarding.field.badge-test.accepted' : 'onboarding.field.badge-test.refused') }}</div>
        <div>{{ badge.physicalReference || badge.name || badge.uid }}<span v-if="badge.physicalReference && badge.uid" class="text--secondary"> · {{ badge.uid }}</span></div>
        <div v-if="badge.owner || badge.organizationName" class="text-body-2">{{ [badge.owner, badge.organizationName].filter(Boolean).join(' · ') }}</div>
        <div v-if="!badge.accepted && (badge.reason || badge.stock)" class="text-body-2">{{ refusalLabel(badge) }}</div>
        <div class="text-caption text--secondary">{{ $options.filters.formatDate(badge.at, 'HH:mm:ss') }}</div>
        <!-- En mode test, un badge sans titulaire devient le badge de test : il portera le membre simulé. -->
        <div v-if="canDesignate(badge)" class="mt-2">
          <v-btn x-small depressed color="primary" :disabled="!simulation" @click="$emit('designate', badge.uid)">
            <v-icon left x-small>mdi-flask</v-icon>{{ $t('onboarding.field.badge-test.use-as-test-badge') }}
          </v-btn>
          <div v-if="!simulation" class="text-caption text--secondary mt-1">{{ $t('onboarding.field.badge-test.choose-member-first') }}</div>
        </div>
        <div v-else-if="isTestBadge(badge)" class="text-caption mt-1">{{ $t('onboarding.field.badge-test.is-test-badge', { member: simulation.memberName }) }}</div>
        <!-- Pendant l'intervention, un badge sans titulaire s'associe à un membre de la borne. -->
        <field-badge-association v-if="canAssociate && intervention && intervention.open && badge.uid && !badge.owner" :intervention-id="intervention.id" :uid="badge.uid" @associated="$emit('associated', $event)" />
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'
import FieldBadgeAssociation from './FieldBadgeAssociation.vue'

const DURATION_S = 120
const POLL_MS = 2000

/**
 * Le test de badge : la personne passe son badge sur la borne pendant que l'écran attend. On lit ce que la borne a
 * demandé depuis le début du test et ce qu'on lui a répondu ; rien n'est envoyé à la borne. Il valide d'un coup le
 * lecteur, la connexion, le badge et ses droits sur cette borne.
 */
export default {
  name: 'FieldBadgeTest',

  components: { FieldBadgeAssociation },

  mixins: [CommissioningMixin],

  props: {
    chargingPointId: { type: String, required: true },
    // L'intervention du technicien sur la borne : en mode test, un badge vierge y devient le badge de test.
    intervention: { type: Object, default: null },
    canManage: { type: Boolean, default: false },
    // Le droit d'attacher un badge à un client (comme au back-office).
    canAssociate: { type: Boolean, default: false },
  },

  data() {
    return { since: null, badges: [], running: false, starting: false, expired: false, remaining: DURATION_S, error: null, timer: null, startedAt: null }
  },

  computed: {
    simulation() {
      return this.intervention?.testMode ? this.intervention.simulation : null
    },
  },

  beforeDestroy() {
    this.stop()
  },

  methods: {
    async start() {
      this.stop()
      Object.assign(this, { badges: [], expired: false, error: null, starting: true })
      try {
        // L'heure du serveur : celle du téléphone peut avoir quelques minutes d'écart.
        this.since = (await FieldRepository.badgeTest(this.chargingPointId)).since
        this.startedAt = Date.now()
        this.running = true
        this.tick()
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.starting = false
      }
    },

    tick() {
      this.timer = setTimeout(async () => {
        const elapsed = Math.floor((Date.now() - this.startedAt) / 1000)
        this.remaining = Math.max(0, DURATION_S - elapsed)
        try {
          this.badges = (await FieldRepository.badgeTest(this.chargingPointId, this.since)).badges || []
        } catch (e) {
          // Réseau coupé un instant : on réessaie au tour suivant.
        }
        // Un badge lu suffit : la personne n'a pas à le repasser.
        if (this.badges.length || this.remaining === 0) {
          this.expired = this.remaining === 0
          this.running = false
          return
        }
        this.tick()
      }, POLL_MS)
    },

    stop() {
      clearTimeout(this.timer)
      this.timer = null
      this.running = false
    },

    // Un badge sans titulaire que le stock connaît : il n'est pas encore attribué. Le refus de l'itinérance, qu'on
    // interroge pour tout badge inconnu du réseau, n'apprendrait rien au technicien.
    refusalLabel(badge) {
      if (badge.stock && !badge.owner) {
        return this.$t('onboarding.field.badge-test.in-stock', { number: badge.stock.printedNumber })
      }
      return this.reasonLabel(badge.reason)
    },

    canDesignate(badge) {
      return this.canManage && !!this.intervention?.testMode && !!badge.uid && !badge.owner && !this.isTestBadge(badge)
    },

    isTestBadge(badge) {
      return !!this.simulation?.badgeUid && (badge.uid || '').toUpperCase() === this.simulation.badgeUid
    },

    reasonLabel(reason) {
      const key = `onboarding.field.badge-test.reason.${reason}`
      return this.$te(key) ? this.$t(key) : reason
    },
  },
}
</script>

<style scoped>
.field-badge-test__title {
  font-weight: 500;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
</style>
